import { useCallback } from 'react';
import { format } from 'components/atoms/input-duration';
import {
  useGetAllTasks,
  ProjectTemplateTask,
  ProjectTemplateProjectPhase,
  useGetAllProjectPhases,
} from 'data/templates/projects';
import { useProjectDynamicTemplateConditions } from 'data/templates/projects/hooks/use-project-dynamic-template';

interface Result {
  totalEfforts: {
    effortsForTasksWithoutDynamicConditions: number;
    effortForAllTasks: number;
  };
  estimatedHoursAsText: string;
  hasDynamicConditions: boolean;
}
export const useEstimatedBudgetHours = (): Result => {
  const allTasks = useGetAllTasks();
  const allPhases = useGetAllProjectPhases();
  const dynamicConditons = useProjectDynamicTemplateConditions();
  const havePhaseDynamicConditions = useCallback(
    (phase: ProjectTemplateProjectPhase) =>
      (dynamicConditons?.[phase.projectPhaseId]?.criteria?.length ?? 0) !== 0,
    [dynamicConditons],
  );
  const haveTaskDynamicConditions = useCallback(
    (task: ProjectTemplateTask) => {
      if ((dynamicConditons?.[task.taskId]?.criteria?.length ?? 0) !== 0) {
        return true;
      }
      if (
        task.parentTask?.taskId != null &&
        allTasks[task.parentTask?.taskId] != null
      ) {
        return haveTaskDynamicConditions(allTasks[task.parentTask?.taskId]);
      }

      if (
        task.projectPhase?.projectPhaseId != null &&
        allPhases[task.projectPhase?.projectPhaseId] != null
      ) {
        return havePhaseDynamicConditions(
          allPhases[task.projectPhase?.projectPhaseId],
        );
      }

      return false;
    },
    [allPhases, allTasks, dynamicConditons, havePhaseDynamicConditions],
  );
  const totalEfforts = Object.values(allTasks).reduce(
    (total, task) => {
      return {
        effortsForTasksWithoutDynamicConditions:
          total.effortsForTasksWithoutDynamicConditions +
          (!haveTaskDynamicConditions(task) ? task.effort ?? 0 : 0),
        effortForAllTasks: total.effortForAllTasks + (task.effort ?? 0),
      };
    },
    {
      effortsForTasksWithoutDynamicConditions: 0,
      effortForAllTasks: 0,
    },
  );
  const isEffortsSameForTaskWithAndWithoutDynamicConditions =
    totalEfforts.effortForAllTasks ===
    totalEfforts.effortsForTasksWithoutDynamicConditions;
  const estimatedHours = isEffortsSameForTaskWithAndWithoutDynamicConditions
    ? format('hh', Math.ceil(totalEfforts.effortForAllTasks / 60))
    : `${format(
        'hh',
        Math.ceil(totalEfforts.effortsForTasksWithoutDynamicConditions / 60),
      )} - ${format('hh', Math.ceil(totalEfforts.effortForAllTasks / 60))}`;

  return {
    totalEfforts,
    estimatedHoursAsText: estimatedHours,
    hasDynamicConditions: !isEffortsSameForTaskWithAndWithoutDynamicConditions,
  };
};
